import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { zodResolver } from '@hookform/resolvers/zod';
import CompanyHeader from '@/components/company/CompanyHeader';
import FormBuilder from '@/components/company/FormBuilder';
import type { FormField } from '@/components/company/FormBuilder';
import GenerateAIModal from '@/components/company/GenerateAIModal';
import AIResultModal from '@/components/company/AIResultModal';
import PositioningAIGenerateForm from './positioning/PositioningAIGenerateForm';
import useCompanyState from '@/states/company.state';
import useTenantState from '@/states/tenant.state';
import { useGeneratePositioning } from './positioning/useGeneratePositioning';
import {
  positioningAIGenerateSchema,
  type PositioningAIGenerateFormData,
} from './positioning/positioning-ai-generate.schema';

interface PositioningFormValues {
  company: string;
  category: string;
  dna: string;
  core_value: string;
  positioning: string;
}

export default function Positioning() {
  const formControl = useForm<PositioningFormValues>({
    defaultValues: {
      company: '',
      category: '',
      dna: '',
      core_value: '',
      positioning: '',
    },
  });
  const { handleSubmit, reset, setValue } = formControl;
  const formRef = useRef<HTMLFormElement>(null);

  const aiFormControl = useForm<PositioningAIGenerateFormData>({
    resolver: zodResolver(positioningAIGenerateSchema),
    defaultValues: {
      company_description: '',
      competitor: '',
      unique_value: '',
      target_audience: '',
    },
  });

  const { companyProfile, updateCompanyField, isLoading } = useCompanyState();
  const { currentTenant } = useTenantState();
  const [isPublished, setIsPublished] = useState(false);
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [isResultModalOpen, setIsResultModalOpen] = useState(false);
  const [generatedOptions, setGeneratedOptions] = useState<string[]>([]);

  const generatePositioningMutation = useGeneratePositioning({
    tenantId: currentTenant?.id ? String(currentTenant.id) : '',
  });

  useEffect(() => {
    if (!companyProfile) return;

    const positioningData = Array.isArray(companyProfile.positioning) ? companyProfile.positioning[0] : null;

    if (positioningData) {
      reset({
        company: positioningData.company ?? '',
        category: positioningData.category ?? '',
        dna: positioningData.dna ?? '',
        core_value: positioningData.core_value ?? '',
        positioning: positioningData.positioning ?? '',
      });
    }

    const publishedValue = companyProfile.is_published_positioning;
    setIsPublished(!!publishedValue);
  }, [companyProfile, reset]);

  const handleSave = async (data: PositioningFormValues) => {
    if (!currentTenant?.id) return;

    const payload = [
      {
        company: data.company,
        category: data.category,
        dna: data.dna,
        core_value: data.core_value,
        positioning: data.positioning,
      },
    ];

    updateCompanyField(currentTenant.id, 'positioning', payload, isPublished);
  };

  const handleOpenGenerateModal = () => {
    aiFormControl.reset();
    setIsGenerateModalOpen(true);
  };

  const handleCloseGenerateModal = () => {
    setIsGenerateModalOpen(false);
  };

  const handleGenerate = async (data: PositioningAIGenerateFormData) => {
    try {
      const result = await generatePositioningMutation.mutateAsync(data);

      if (result.success && result.data?.generated_options) {
        setGeneratedOptions(result.data.generated_options);
        setIsGenerateModalOpen(false);
        setIsResultModalOpen(true);
        toast.success('Positioning generated successfully');
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to generate positioning');
    }
  };

  const handleCloseResultModal = () => {
    setIsResultModalOpen(false);
    setGeneratedOptions([]);
  };

  const handleSaveGeneratedPositioning = (selectedOption: string) => {
    setValue('positioning', selectedOption);
    setIsResultModalOpen(false);
    setGeneratedOptions([]);
  };

  const handleAfterSave = async () => {
    if (!currentTenant?.id) return;
    const currentData = formControl.getValues();
    const payload = [
      {
        company: currentData.company,
        category: currentData.category,
        dna: currentData.dna,
        core_value: currentData.core_value,
        positioning: currentData.positioning,
      },
    ];
    await updateCompanyField(currentTenant.id, 'positioning', payload, isPublished);
    toast.success('Positioning saved successfully');
  };

  const fields: FormField[] = [
    { name: 'company', label: 'Company', type: 'markdown', placeholder: 'Enter company name or description...' },
    { name: 'category', label: 'Business Category', type: 'markdown', placeholder: 'Enter business category...' },
    { name: 'dna', label: 'DNA', type: 'markdown', placeholder: "Describe your company's DNA..." },
    { name: 'core_value', label: 'Core Value', type: 'markdown', placeholder: "Define your company's core values..." },
    {
      name: 'positioning',
      label: 'Positioning',
      type: 'markdown',
      placeholder: "Describe your company's market position...",
    },
  ];

  return (
    <section className="w-full">
      <CompanyHeader
        title="Market Positioning"
        description="Define how your company positions itself in the market and industry."
        onGenerate={handleOpenGenerateModal}
        generateLabel="Generate"
        saveLabel="Save"
        onSave={() => formRef.current?.requestSubmit()}
        isSaving={isLoading}
      />

      <div className="mx-5 border-b border-gray-200" />

      <FormProvider {...formControl}>
        <form ref={formRef} onSubmit={handleSubmit(handleSave)} className="pt-5">
          <FormBuilder fields={fields} initialPublished={isPublished} onPublishChange={setIsPublished} />
        </form>
      </FormProvider>

      <GenerateAIModal
        isOpen={isGenerateModalOpen}
        onClose={handleCloseGenerateModal}
        onSubmit={aiFormControl.handleSubmit(handleGenerate)}
        title="Generate Positioning with AI"
        description="Provide the information below to help AI generate positioning that suits your company."
        generateButtonLabel="Generate Positioning"
        isLoading={generatePositioningMutation.isLoading}
      >
        <PositioningAIGenerateForm
          control={aiFormControl.control}
          errors={aiFormControl.formState.errors}
          isLoading={generatePositioningMutation.isLoading}
        />
      </GenerateAIModal>

      <AIResultModal
        isOpen={isResultModalOpen}
        onClose={handleCloseResultModal}
        options={generatedOptions}
        onSave={handleSaveGeneratedPositioning}
        onAfterSave={handleAfterSave}
        title="AI Generated Positioning"
        description="Select the positioning that best represents your company"
        saveButtonLabel="Save to Profile"
        isLoading={isLoading}
      />
    </section>
  );
}
